import { computed, onMounted, reactive, ref } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import expenseService, { type ExpenseCategory } from "@/services/expense";

type ExpenseCategoryForm = {
  code: string;
  name: string;
  description: string;
};

const createInitialForm = (): ExpenseCategoryForm => ({
  code: "",
  name: "",
  description: "",
});

const normalizeSearch = (value: string) => value.replace(/\s+/g, " ").trim().toLowerCase();

const generateExpenseCategoryCode = (existingCodes: string[]) => {
  const normalizedCodes = new Set(existingCodes.map((code) => code.trim().toUpperCase()));
  let nextNumber = normalizedCodes.size + 1;

  while (normalizedCodes.has(`EXC-${String(nextNumber).padStart(3, "0")}`)) {
    nextNumber += 1;
  }

  return `EXC-${String(nextNumber).padStart(3, "0")}`;
};

export function useExpenseCategory() {
  const { t } = useI18n();
  const searchQuery = ref("");
  const loading = ref(false);
  const saving = ref(false);
  const categories = ref<ExpenseCategory[]>([]);
  const selectedCategory = ref<ExpenseCategory | null>(null);
  const form = reactive<ExpenseCategoryForm>(createInitialForm());

  const isEditMode = computed(() => Boolean(selectedCategory.value?.id));

  const filteredCategories = computed(() => {
    const query = normalizeSearch(searchQuery.value);

    if (!query) return categories.value;

    return categories.value.filter((category) => {
      const name = String(category.name || "").toLowerCase();
      const code = String(category.code || "").toLowerCase();
      const description = String(category.description || "").toLowerCase();

      return name.includes(query) || code.includes(query) || description.includes(query);
    });
  });

  const resetForm = () => {
    Object.assign(form, createInitialForm());
  };

  const fillForm = (category: ExpenseCategory | null) => {
    Object.assign(form, {
      code: category?.code || "",
      name: category?.name || "",
      description: category?.description || "",
    });
  };

  const hideModal = () => {
    const modalElement = document.getElementById("expenseCategoryModal");
    if (!modalElement) return;

    const BootstrapModal = Modal as any;
    const modalInstance =
      BootstrapModal.getInstance?.(modalElement) ||
      BootstrapModal.getOrCreateInstance?.(modalElement) ||
      new Modal(modalElement);

    modalInstance.hide();
  };

  const fetchCategories = async () => {
    loading.value = true;
    try {
      categories.value = await expenseService.getExpenseCategories();
    } catch (error) {
      console.error("Error fetching expense categories:", error);
      Swal.fire({
        icon: "error",
        title: t("expense_category_page.failed_to_load"),
        text: t("expense_category_page.unable_to_load_expense_categories"),
      });
    } finally {
      loading.value = false;
    }
  };

  const openCreateModal = () => {
    selectedCategory.value = null;
    resetForm();
    form.code = generateExpenseCategoryCode(categories.value.map((category) => category.code || ""));
  };

  const openEditModal = (category: ExpenseCategory) => {
    selectedCategory.value = category;
    fillForm(category);
  };

  const updateField = (field: keyof ExpenseCategoryForm, value: string) => {
    form[field] = value;
  };

  const generateCode = () => {
    form.code = generateExpenseCategoryCode(categories.value.map((category) => category.code || ""));
  };

  const submitCategory = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      await Swal.fire({
        icon: "warning",
        title: t("expense_category_page.missing_fields"),
        text: t("expense_category_page.code_and_name_required"),
      });
      return;
    }

    saving.value = true;
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
      };

      if (isEditMode.value && selectedCategory.value?.id) {
        await expenseService.updateExpenseCategory(selectedCategory.value.id, payload);
      } else {
        await expenseService.createExpenseCategory(payload);
      }

      hideModal();
      await fetchCategories();

      await Swal.fire({
        icon: "success",
        title: isEditMode.value ? t("expense_category_page.category_updated") : t("expense_category_page.category_created"),
        text: isEditMode.value
          ? t("expense_category_page.category_updated_success")
          : t("expense_category_page.category_created_success"),
        timer: 1600,
        showConfirmButton: false,
      });

      selectedCategory.value = null;
      resetForm();
    } catch (error: any) {
      console.error("Error saving expense category:", error);

      let message = t("expense_category_page.failed_to_save_expense_category");
      const apiMessage = error?.response?.data?.message;
      const validationErrors = error?.response?.data?.errors;

      if (apiMessage) {
        message = apiMessage;
      }

      if (validationErrors) {
        const lines = Object.values(validationErrors).flat().join("\n");
        if (lines) {
          message = `${message}\n${lines}`;
        }
      }

      await Swal.fire({
        icon: "error",
        title: t("expense_category_page.save_failed"),
        text: message,
      });
    } finally {
      saving.value = false;
    }
  };

  const deleteCategory = async (category: ExpenseCategory) => {
    if (!category.id) return;

    const result = await Swal.fire({
      title: t("expense_category_page.delete_category_title"),
      text: t("expense_category_page.delete_category_text", { name: category.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("expense_category_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await expenseService.deleteExpenseCategory(category.id);
      await fetchCategories();

      await Swal.fire({
        icon: "success",
        title: t("expense_category_page.deleted"),
        text: t("expense_category_page.expense_category_deleted_successfully"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting expense category:", error);
      await Swal.fire({
        icon: "error",
        title: t("expense_category_page.delete_failed"),
        text: t("expense_category_page.unable_to_delete_expense_category"),
      });
    }
  };

  onMounted(fetchCategories);

  return {
    searchQuery,
    loading,
    saving,
    form,
    isEditMode,
    categories: filteredCategories,
    openCreateModal,
    openEditModal,
    updateField,
    generateCode,
    submitCategory,
    deleteCategory,
  };
}
